import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Html5QrcodeScanner } from "html5-qrcode";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { useUserData } from "../hooks/useUserData";
import { QrCode, MapPin, CheckCircle, AlertTriangle, RefreshCw, ArrowLeft, Wrench } from "lucide-react";
import BottomNavTechnician from "../components/technician/BottomNavTechnician";

export default function TechnicianQRScanner() {
  const [user] = useUserData();
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState("");
  const [scannedAsset, setScannedAsset] = useState(null);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [toast, setToast] = useState("");
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const scannerRef = useRef(null);

  useEffect(() => {
    if (!scanning) return;

    // Set mounted flag
    isMountedRef.current = true;

    const scanner = new Html5QrcodeScanner("reader", {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    });

    // Store scanner reference
    scannerRef.current = scanner;

    const onScanSuccess = async (decodedText) => {
      // Don't process if component is unmounting
      if (!isMountedRef.current) return;

      try {
        let assetId;
        if (decodedText.includes('/asset/')) {
          const url = new URL(decodedText);
          assetId = url.pathname.split('/asset/')[1];
        } else if (decodedText.includes('id=')) {
          const url = new URL(decodedText);
          assetId = url.searchParams.get('id');
        } else {
          assetId = decodedText;
        }

        if (!assetId) {
          setError("Invalid QR code: No asset ID found");
          return;
        }

        const assetDoc = await getDoc(doc(db, "assets", assetId));
        if (!assetDoc.exists()) {
          setError("Asset not found in database");
          return;
        }

        const assetData = { id: assetId, ...assetDoc.data() };
        setScannedAsset(assetData);

        // Fetch maintenance tasks for this asset
        const tasksQuery = query(
          collection(db, "maintenanceTasks"),
          where("assetId", "==", assetId)
        );
        const tasksSnap = await getDocs(tasksQuery);
        const tasksData = tasksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMaintenanceTasks(tasksData);

        setScanning(false);

        // Only clear scanner if component is still mounted
        if (isMountedRef.current && scannerRef.current) {
          scannerRef.current.clear().catch(err => {
            // Ignore cleanup errors when component is unmounting
            if (isMountedRef.current) {
              console.error("Failed to clear scanner:", err);
            }
          });
        }
      } catch (err) {
        console.error("Error processing QR code:", err);
        setError("Invalid QR code format");
      }
    };

    const onScanFailure = (error) => {
      if (!error.includes("No MultiFormat Readers")) {
        console.warn("QR Scan error:", error);
      }
    };

    scanner.render(onScanSuccess, onScanFailure);

    return () => {
      // Mark as unmounting
      isMountedRef.current = false;

      // Clear scanner only if it exists and we're still mounted
      if (scannerRef.current) {
        scannerRef.current.clear().catch(err => {
          // Ignore cleanup errors during unmount
          console.debug("Scanner cleanup during unmount:", err.message);
        });
        scannerRef.current = null;
      }
    };
  }, [scanning]);



  const handleViewTaskDetails = (taskId) => {
    navigate(`/technician/task/${taskId}`);
  };

  const restartScanning = () => {
    setError("");
    setScannedAsset(null);
    setMaintenanceTasks([]);
    setScanning(true);
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 animate-fade-in">
      <div className="w-full px-4 pt-20 pb-6 lg:px-8 xl:px-12">
        <div className="max-w-md mx-auto space-y-6">
          {/* Header Section */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <QrCode className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">
              Scan Asset QR Code
            </h1>
            <p className="text-neutral-600">
              Point your camera at the asset's QR code to access maintenance tasks
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-xl p-4 animate-slide-in">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-error-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-error-900">Scanning Error</p>
                  <p className="text-sm text-error-700 mt-1">{error}</p>
                  <button
                    onClick={restartScanning}
                    className="btn-error mt-3 px-4 py-2 text-sm"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Toast */}
          {toast && (
            <div className="bg-success-50 border border-success-200 rounded-xl p-4 animate-bounce-in">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-success-600" />
                <p className="text-success-900 font-medium">{toast}</p>
              </div>
            </div>
          )}

          {scannedAsset ? (
            <div className="space-y-6 animate-fade-in">
              {/* Asset Info Card */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-neutral-900">
                    {scannedAsset.name || "Unnamed Asset"}
                  </h2>
                  <span className={`status-${scannedAsset.status?.toLowerCase() === 'available' ? 'success' : scannedAsset.status?.toLowerCase() === 'in-use' || scannedAsset.status?.toLowerCase() === 'borrowed' ? 'info' : scannedAsset.status?.toLowerCase() === 'missing' ? 'error' : 'warning'}`}>
                    {scannedAsset.status || "Unknown"}
                  </span>
                </div>

                <div className="space-y-3 text-sm text-neutral-600">
                  <div className="flex items-center gap-3">
                    <QrCode className="w-4 h-4 text-neutral-500" />
                    <span>ID: {scannedAsset.id}</span>
                  </div>
                  {scannedAsset.location && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-neutral-500" />
                      <span>Location: {scannedAsset.location}</span>
                    </div>
                  )}
                  {scannedAsset.category && (
                    <div className="flex items-center gap-3">
                      <span>Category: {scannedAsset.category}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Maintenance Tasks */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-primary-600" />
                  Maintenance Tasks
                </h3>

                {maintenanceTasks.length === 0 ? (
                  <div className="card text-center py-8">
                    <Wrench className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-neutral-900 mb-2">No Maintenance Tasks</h4>
                    <p className="text-neutral-600 text-sm">
                      This asset doesn't have any active maintenance tasks.
                    </p>
                  </div>
                ) : (
                  maintenanceTasks.map((task) => (
                    <div key={task.id} className="card-interactive p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-neutral-900 mb-1">{task.description}</h4>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              task.priority === "high" ? "bg-error-100 text-error-700" :
                              task.priority === "medium" ? "bg-warning-100 text-warning-700" :
                              "bg-info-100 text-info-700"
                            }`}>
                              {task.priority} Priority
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              task.status === "pending" ? "bg-warning-100 text-warning-700" :
                              task.status === "in-progress" ? "bg-info-100 text-info-700" :
                              task.status === "completed" ? "bg-success-100 text-success-700" :
                              "bg-neutral-100 text-neutral-700"
                            }`}>
                              {task.status}
                            </span>
                          </div>
                          {task.dueDate && (
                            <p className="text-xs text-neutral-500">
                              Due: {new Date(task.dueDate.toDate()).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewTaskDetails(task.id)}
                        className="btn-primary w-full py-2 text-sm"
                      >
                        View Task Details
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  onClick={restartScanning}
                  className="btn-outline w-full py-3 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Scan Another QR Code
                </button>
              </div>
            </div>
          ) : (
            <div className="card text-center animate-fade-in">
              <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-neutral-400" />
              </div>
              <div id="reader" className="w-full mb-4"></div>
              <p className="text-neutral-500 text-sm">
                Position QR code within the frame
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
